/**
 * VendorPortalProductVariantService
 * Service for vendor product variant management.
 * Route: api/vendor-portal/products/{productId}/variants
 */
import { VendorPortalBaseService } from './VendorPortalBaseService';
/**
 * VendorPortalProductVariantService
 * Handles vendor product variant operations for the vendor portal.
 * Maps to /api/vendor-portal/products/{productId}/variants endpoint
 */
export class VendorPortalProductVariantService extends VendorPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorPortalProductVariantService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? VendorPortalProductVariantService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all variants for a specific product
     * GET /api/vendor-portal/products/{productId}/variants
     */
    async getVariants(productId) {
        const response = await this.getVariantsRaw(productId);
        return response.data ?? [];
    }
    /**
     * Get all variants (raw response)
     */
    getVariantsRaw(productId) {
        return this.get(`${productId}/variants`);
    }
    // ==================== GET BY ID ====================
    /**
     * Get a specific variant by ID
     * GET /api/vendor-portal/products/{productId}/variants/{variantId}
     */
    async getVariant(productId, variantId) {
        const response = await this.getVariantRaw(productId, variantId);
        if (!response.data) {
            throw new Error(`Variant with ID ${variantId} not found`);
        }
        return response.data;
    }
    /**
     * Get a specific variant (raw response)
     */
    getVariantRaw(productId, variantId) {
        return this.get(`${productId}/variants/${variantId}`);
    }
    // ==================== CREATE ====================
    /**
     * Create a new variant for a product
     * POST /api/vendor-portal/products/{productId}/variants
     */
    async createVariant(productId, data) {
        const response = await this.createVariantRaw(productId, data);
        if (!response.data) {
            throw new Error('Failed to create variant');
        }
        return response.data;
    }
    /**
     * Create a new variant (raw response)
     */
    createVariantRaw(productId, data) {
        return this.post(`${productId}/variants`, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing variant
     * PUT /api/vendor-portal/products/{productId}/variants/{variantId}
     */
    async updateVariant(productId, variantId, data) {
        const response = await this.updateVariantRaw(productId, variantId, data);
        if (!response.data) {
            throw new Error(`Failed to update variant with ID ${variantId}`);
        }
        return response.data;
    }
    /**
     * Update an existing variant (raw response)
     */
    updateVariantRaw(productId, variantId, data) {
        return this.put(`${productId}/variants/${variantId}`, data);
    }
    // ==================== DELETE ====================
    /**
     * Delete a variant
     * DELETE /api/vendor-portal/products/{productId}/variants/{variantId}
     */
    async deleteVariant(productId, variantId) {
        const response = await this.deleteVariantRaw(productId, variantId);
        return response.success;
    }
    /**
     * Delete a variant (raw response)
     */
    deleteVariantRaw(productId, variantId) {
        return this.delete(`${productId}/variants/${variantId}`);
    }
}
VendorPortalProductVariantService.DEFAULT_SERVICE_PATH = 'vendor-catalogs';
VendorPortalProductVariantService.BASE_PATH = 'api/vendor-portal/products';
//# sourceMappingURL=VendorPortalProductVariantService.js.map